import React, { ReactElement, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { lazyWithRetries, LoadingScreen } from '@mesa-labs/mesa-ui';
import { types as api } from '@mesa-labs/mesa-api';

import CognitoRoute from './common/CognitoRoute';
import { AuthenticationGroup } from '../redux/slices/auth';
import ExternalClientsPageTemplate from '../components/common/ExternalClientsPageTemplate';
import { BusinessEntityScope } from '@mesa-labs/mesa-api/dist/types';
import ExternalClientVendorsPageTemplate from '../components/common/ExternalClientVendors';
import ExternalVendorsPageTemplate from '../components/common/ExternalVendorsPageTemplate';
import ExternalClientPageTemplate from '../components/common/ExternalClientPageTemplate';
import ExternalVendorPageTemplate from '../components/common/ExternalVendorPageTemplate';
import VendorActivityPage from '../components/pages/Vendor/Activity';

const AcceleratedPayoutStatisticsPage = lazyWithRetries(() => import('../components/pages/AcceleratedPayout'));
const EligibleInvoicesPage = lazyWithRetries(() => import('../components/pages/EligibleInvoices'));
const AnomaliesPage = lazyWithRetries(() => import('../components/pages/Anomalies'));
const AnomalyPage = lazyWithRetries(() => import('../components/pages/Anomaly'));
const PartnerIntegrations = lazyWithRetries(() => import('../components/pages/PartnerIntegrations'));

const ClientsPage = lazyWithRetries(() => import('../components/pages/Clients'));
const ClientsClientsPage = lazyWithRetries(() => import('../components/pages/Clients/Clients'));
const ClientsClientPage = lazyWithRetries(() => import('../components/pages/Clients/Client'));
const ClientsClientMappingsPage = lazyWithRetries(() => import('../components/pages/Clients/ClientMappings'));
const ClientsClientMappingPage = lazyWithRetries(() => import('../components/pages/Clients/ClientMapping'));
const ClientsExclusionsPage = lazyWithRetries(() => import('../components/pages/Clients/ClientExclusions'));
const ClientsClientExclusionPage = lazyWithRetries(() => import('../components/pages/Clients/ClientExclusion'));
const ClientsVendorClientRestrictionsPage = lazyWithRetries(() => import('../components/pages/Clients/VendorClientRestrictions'));
const ClientsConsolidatedClientsPage = lazyWithRetries(() => import('../components/pages/Clients/ConsolidatedClients'));
const VendorOverviewPage = lazyWithRetries(() => import('../components/pages/Vendor/Overview'));
const VendorUnderwritingPage = lazyWithRetries(() => import('../components/pages/Vendor/Underwriting'));
const VendorUnderwritingOverviewPage = lazyWithRetries(() => import('../components/pages/Vendor/UnderwritingOverview'));
const VendorUnderwritingDashboardPage = lazyWithRetries(() => import('../components/pages/Vendor/UnderwritingDashboard'));
const VendorUnderwritingTransactionsPage = lazyWithRetries(() => import('../components/pages/Vendor/UnderwritingTransactions'));
const VendorUnderwritingCoveragePage = lazyWithRetries(() => import('../components/pages/Vendor/UnderwritingCoverage'));
const VendorDetailedFinancialsPage = lazyWithRetries(() => import('../components/pages/Vendor/DetailedFinancials'));
const BankAccountsPage = lazyWithRetries(() => import('../components/pages/Vendor/BankAccounts'));
const VendorNotesPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorNotes'));
const VendorUccsPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorUccs'));
const UccsPage = lazyWithRetries(() => import('../components/pages/Uccs'));
const VendorSubAccountsPage = lazyWithRetries(() => import('../components/pages/VendorSubAccounts'));
const VendorAuditPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorAudit'));
const VendorVerificationDocumentsPage = lazyWithRetries(() => import('../components/pages/Vendor/VerificationDocuments'));
const VendorAdjustmentGroupsPage = lazyWithRetries(() => import('../components/pages/Vendor/AdjustmentGroups'));
const VendorServiceAgreementsPage = lazyWithRetries(() => import('../components/pages/Vendor/ServiceAgreements'));
const VendorUserAccountsPage = lazyWithRetries(() => import('../components/pages/Vendor/UserAccounts'));
const VendorInvoicesPage = lazyWithRetries(() => import('../components/pages/Vendor/Invoices'));
const VendorInFlightsPage = lazyWithRetries(() => import('../components/pages/Vendor/InFlights'));
const VendorEventsPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorEvents'));
const VendorEmailSequencesPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorEmailSequences'));
const VendorEmailSequenceDetailPage = lazyWithRetries(() => import('../components/pages/Vendor/VendorEmailSequenceDetailPage'));
const VendorFeaturesPage = lazyWithRetries(() => import('../components/pages/Vendor/Features'));
const VendorScheduledVendorCollectionsPage = lazyWithRetries(() => import('../components/pages/Vendor/ScheduledVendorCollections'));
const VendorRestrictionsPage = lazyWithRetries(() => import('../components/pages/Vendor/Restrictions'));
const VendorClientMappingOverridesPage = lazyWithRetries(() => import('../components/pages/Vendor/ClientMappingOverrides'));
const ProgramPage = lazyWithRetries(() => import('../components/pages/Program'));
const ProgramsPage = lazyWithRetries(() => import('../components/pages/Programs'));
const PartnerServiceAgreements = lazyWithRetries(() => import('../components/pages/PartnerServiceAgreements'));
const PartnerRevSharesPage = lazyWithRetries(() => import('../components/pages/PartnerRevShares'));
const PartnerRevSharePage = lazyWithRetries(() => import('../components/pages/PartnerRevShares/PartnerRevShare'));
const UpdatePartnerServiceAgreementPage = lazyWithRetries(() => import('../components/pages/UpdatePartnerServiceAgreementPage'));
const CreatePartnerServiceAgreementPage = lazyWithRetries(() => import('../components/pages/CreatePartnerServiceAgreementPage'));
const InvoiceDocumentPage = lazyWithRetries(() => import('../components/pages/InvoiceDocument'));
const VendorDocumentsPage = lazyWithRetries(() => import('../components/pages/VendorDocuments'));

const PartnersPage = lazyWithRetries(() => import('../components/pages/Partners'));
const PartnerPage = lazyWithRetries(() => import('../components/pages/Partner'));
const PartnerTeamMembersPage = lazyWithRetries(() => import('../components/pages/Partner/TeamMembers'));
const PartnerDefaultPermissionsPage = lazyWithRetries(() => import('../components/pages/Partner/DefaultPermissions'));

const HomePage = lazyWithRetries(() => import('../components/pages/Home'));
const SignInPage = lazyWithRetries(() => import('../components/pages/SignIn'));
const VendorsPage = lazyWithRetries(() => import('../components/pages/Vendors'));
const VendorPage = lazyWithRetries(() => import('../components/pages/Vendor'));
const OnboardingsPage = lazyWithRetries(() => import('../components/pages/Onboardings'));
const InvoicesPage = lazyWithRetries(() => import('../components/pages/Invoices'));
const InvoiceApprovalsPage = lazyWithRetries(() => import('../components/pages/InvoiceApprovals'));
const InvoiceQuarantinesPage = lazyWithRetries(() => import('../components/pages/InvoiceQuarantines'));
const InvoicePage = lazyWithRetries(() => import('../components/pages/Invoice'));
const InvoiceLineItemsPage = lazyWithRetries(() => import('../components/pages/InvoiceLineItems'));
const InvoiceLineItemPage = lazyWithRetries(() => import('../components/pages/InvoiceLineItem'));
const OnboardingPage = lazyWithRetries(() => import('../components/pages/Onboarding'));
const ServiceAgreements = lazyWithRetries(() => import('../components/pages/ServiceAgreements'));
const CreateServiceAgreementPage = lazyWithRetries(() => import('../components/pages/CreateServiceAgreementPage'));
const CollectionsPage = lazyWithRetries(() => import('../components/pages/Collections'));
const CollectionPage = lazyWithRetries(() => import('../components/pages/Collection'));
const FactoringCollectionsPage = lazyWithRetries(() => import('../components/pages/FactoringCollections'));
const AdjustmentsPage = lazyWithRetries(() => import('../components/pages/Adjustments'));
const ProjectionsPage = lazyWithRetries(() => import('../components/pages/Projections'));
const TasksPage = lazyWithRetries(() => import('../components/pages/Tasks'));
const BatchSweepsPage = lazyWithRetries(() => import('../components/pages/BatchSweeps'));
const ReportsPage = lazyWithRetries(() => import('../components/pages/Reports'));
const BatchSweepPage = lazyWithRetries(() => import('../components/pages/BatchSweep'));
const ReconciliationPage = lazyWithRetries(() => import('../components/pages/Reconciliation'));
const ReconciliationPayloadPage = lazyWithRetries(() => import('../components/pages/ReconciliationPayload'));
const SettingsPage = lazyWithRetries(() => import('../components/pages/Settings'));
const TransfersPage = lazyWithRetries(() => import('../components/pages/Transfers'));
const VendorServiceAgreementPage = lazyWithRetries(() => import('../components/pages/VendorServiceAgreement'));
const ExternalVendorStatistics = lazyWithRetries(() => import('../components/pages/ExternalVendorStatistics'));
const ExternalInvoicesPage = lazyWithRetries(() => import('../components/pages/ExternalInvoices'));
const DataReconciliationPage = lazyWithRetries(() => import('../components/pages/DataReconciliation'));
const UpdateServiceAgreementPage = lazyWithRetries(() => import('../components/pages/UpdateServiceAgreementPage'));
const KeyPerformanceIndicatorsPage = lazyWithRetries(() => import('../components/pages/KeyPerformanceIndicators'));
const ConfigurationSettingsPage = lazyWithRetries(() => import('../components/pages/ConfigurationSettingsPage'));
const VendorDocumentPage = lazyWithRetries(() => import('../components/pages/VendorDocument'));
const EnablementV2Page = lazyWithRetries(() => import('../components/pages/EnablementV2'));
const AdjustmentPage = lazyWithRetries(() => import('../components/pages/Adjustment'));
const FacilitatorsPage = lazyWithRetries(() => import('../components/pages/Facilitators'));
const ProgramPromotionsPage = lazyWithRetries(() => import('../components/pages/ProgramPromotions'));
const ProgramPromotionDetailPage = lazyWithRetries(() => import('../components/pages/ProgramPromotions/Detail'));
const EmailSequencesLayout = lazyWithRetries(() => import('../components/pages/EmailSequences/EmailSequencesLayout'));
const EmailSequencesPage = lazyWithRetries(() => import('../components/pages/EmailSequences'));
const VendorEmailSequencesOverviewPage = lazyWithRetries(() => import('../components/pages/VendorEmailSequences'));
const EmailSequenceDetailPage = lazyWithRetries(() => import('../components/pages/EmailSequences/Detail'));
const EmailSequenceAuditLogPage = lazyWithRetries(() => import('../components/pages/EmailSequenceAuditLog'));
const EmailSequenceAuditLogEntryPage = lazyWithRetries(() => import('../components/pages/EmailSequenceAuditLog/Detail'));
const EmailTemplatesLayout = lazyWithRetries(() => import('../components/pages/EmailTemplates/EmailTemplatesLayout'));
const EmailTemplatesPage = lazyWithRetries(() => import('../components/pages/EmailTemplates'));
const EmailTemplateConditionsPage = lazyWithRetries(() => import('../components/pages/EmailTemplates/TemplateConditions'));
const FaqsLayout = lazyWithRetries(() => import('../components/pages/Faqs/FaqsLayout'));
const FaqConfigurationPage = lazyWithRetries(() => import('../components/pages/Faqs'));
const FaqDefaultsPage = lazyWithRetries(() => import('../components/pages/Faqs/FaqDefaultsPage'));
const FaqAuditPage = lazyWithRetries(() => import('../components/pages/Faqs/FaqAuditPage'));
const FactoringAccessPage = lazyWithRetries(() => import('../components/pages/FactoringAccess'));
const AdjustmentGroupPage = lazyWithRetries(() => import('../components/pages/AdjustmentGroup'));
const InvoiceAdjustmentApprovals = lazyWithRetries(() => import('../components/pages/InvoiceAdjustmentApprovals'));
const RevenuePage = lazyWithRetries(() => import('../components/pages/Revenue'));
const CohortsPage = lazyWithRetries(() => import('../components/pages/Cohorts'));
const ActionItemsBoardPage = lazyWithRetries(() => import('../components/pages/ActionItems/ActionItemsBoardPage'));
const SvbLoanTapeReportPage = lazyWithRetries(() => import('../components/pages/SvbLoanTapeReport'));
const YieldMonitoringPlatformPage = lazyWithRetries(() => import('../components/pages/YieldMonitoringPlatform'));
const AbandonedUsersPage = lazyWithRetries(() => import('../components/pages/AbandonedUsers'));
const AiAssistantPage = lazyWithRetries(() => import('../components/pages/AiAssistant'));
const GenerateMsaPreviewPage = lazyWithRetries(() => import('../components/pages/GenerateMsaPreview'));
const VendorCsvImportPage = lazyWithRetries(() => import('../components/pages/VendorCsvImport'));
const VendorSurveysPage = lazyWithRetries(() => import('../components/pages/VendorSurveys'));
const BankAccountUnderwritingPage = lazyWithRetries(() => import('../components/pages/Vendor/BankAccountUnderwriting'));
const BankAccountUnderwritingOverviewPage = lazyWithRetries(() => import('../components/pages/Vendor/BankAccountUnderwriting/Overview'));
const BankAccountUnderwritingTransactionsPage = lazyWithRetries(() => import('../components/pages/Vendor/BankAccountUnderwriting/Transactions'));
const BankAccountUnderwritingMatchingPage = lazyWithRetries(() => import('../components/pages/Vendor/BankAccountUnderwriting/Matching'));
const VendorTransactionMatchingPage = lazyWithRetries(() => import('../components/pages/Vendor/TransactionMatching'));
const ScheduledVendorCollections = lazyWithRetries(() => import('../components/pages/ScheduledVendorCollections'));
const PermissionsPage = lazyWithRetries(() => import('../components/pages/Permissions'));
const ScheduledVendorCollectionPage = lazyWithRetries(() => import('../components/pages/ScheduledVendorCollection'));
const InvoiceScheduledInvoiceCollectionsPage = lazyWithRetries(() => import('../components/pages/InvoiceScheduledInvoiceCollections'));
const GlobalSearchResultsPage = lazyWithRetries(() => import('../components/pages/GlobalSearchResults'));
const UnderwritingPage = lazyWithRetries(() => import('../components/pages/Underwriting'));
const TransactionLabelsPage = lazyWithRetries(() => import('../components/pages/Underwriting/TransactionLabels'));
const TransactionLabelDetailPage = lazyWithRetries(() => import('../components/pages/Underwriting/TransactionLabelDetail'));
const TransactionLabelCoveragePage = lazyWithRetries(() => import('../components/pages/Underwriting/TransactionLabelCoverage'));
const TransactionMatchExplorerPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer'));
const TransactionMatchingDashboardPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/DashboardPage'));
const TransactionMatchExplorerMatchesPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/MatchesPage'));
const TransactionMatchExplorerRunsPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/MatchingRunsPage'));
const TransactionMatchExplorerParametersPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/ParametersPage'));
const TransactionMatchExplorerParameterDetailPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/ParameterDetailPage'));
const TransactionMatchDetailPage = lazyWithRetries(() => import('../components/pages/TransactionMatchExplorer/MatchDetailPage'));
const PortfolioMonitoringPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring'));
const PortfolioMonitoringOverviewPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/OverviewPage'));
const PortfolioMonitoringVendorsPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/VendorsPage'));
const PortfolioMonitoringBuyersPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/BuyersPage'));
const PortfolioMonitoringTradingPartnersPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/TradingPartnersPage'));
const PortfolioMonitoringLimitsPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/LimitsPage'));
const PortfolioMonitoringControlsPage = lazyWithRetries(() => import('../components/pages/PortfolioMonitoring/ControlsPage'));

function AppRoutes(): ReactElement {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route
          path="/sign-in"
          element={(
            <CognitoRoute redirectToSignIn={false}>
              <SignInPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/"
          element={(
            <CognitoRoute>
              <HomePage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendors"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/external-vendors"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalVendorsPageTemplate businessEntityScope={BusinessEntityScope.External} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/internal-vendors"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalVendorsPageTemplate businessEntityScope={BusinessEntityScope.Internal} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partners"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <PartnersPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partners/:partnerId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <PartnerPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="team-members" replace />}
          />

          <Route
            path="/partners/:partnerId/team-members"
            element={<PartnerTeamMembersPage />}
          />

          <Route
            path="/partners/:partnerId/default-permissions"
            element={<PartnerDefaultPermissionsPage />}
          />
        </Route>

        <Route
          path="/partners/:partnerId/external-vendors/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalVendorPageTemplate businessEntityScope={BusinessEntityScope.External} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partners/:partnerId/internal-vendors/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalVendorPageTemplate businessEntityScope={BusinessEntityScope.Internal} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/external-clients"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientsPageTemplate businessEntityScope={BusinessEntityScope.External} />
            </CognitoRoute>
          )}
        />
        <Route
          path="/internal-clients"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientsPageTemplate businessEntityScope={BusinessEntityScope.Internal} />
            </CognitoRoute>
          )}
        />
        <Route
          path="/external-client-vendors"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientVendorsPageTemplate businessEntityScope={BusinessEntityScope.External} />
            </CognitoRoute>
          )}
        />
        <Route
          path="/internal-client-vendors"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientVendorsPageTemplate businessEntityScope={BusinessEntityScope.Internal} />
            </CognitoRoute>
          )}
        />
        <Route
          path="/partners/:partnerId/external-clients/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientPageTemplate businessEntityScope={api.BusinessEntityScope.External} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partners/:partnerId/internal-clients/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalClientPageTemplate businessEntityScope={api.BusinessEntityScope.Internal} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendors/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<VendorOverviewPage />}
          />

          <Route
            path="/vendors/:id/overview"
            element={<VendorOverviewPage />}
          />

          <Route
            path="/vendors/:id/bank-accounts"
            element={<BankAccountsPage />}
          />

          <Route
            path="/vendors/:id/bank-accounts/:vendorAccountId/underwriting"
            element={<BankAccountUnderwritingPage />}
          >
            <Route
              index
              element={<BankAccountUnderwritingOverviewPage />}
            />

            <Route
              path="/vendors/:id/bank-accounts/:vendorAccountId/underwriting/overview"
              element={<BankAccountUnderwritingOverviewPage />}
            />

            <Route
              path="/vendors/:id/bank-accounts/:vendorAccountId/underwriting/transactions"
              element={<BankAccountUnderwritingTransactionsPage />}
            />

            <Route
              path="/vendors/:id/bank-accounts/:vendorAccountId/underwriting/matching"
              element={<BankAccountUnderwritingMatchingPage />}
            />
          </Route>

          <Route
            path="/vendors/:id/underwriting"
            element={<VendorUnderwritingPage />}
          >
            <Route
              index
              element={<VendorUnderwritingOverviewPage />}
            />

            <Route
              path="/vendors/:id/underwriting/overview"
              element={<VendorUnderwritingOverviewPage />}
            />

            <Route
              path="/vendors/:id/underwriting/dashboard"
              element={<VendorUnderwritingDashboardPage />}
            />

            <Route
              path="/vendors/:id/underwriting/transactions"
              element={<VendorUnderwritingTransactionsPage />}
            />

            <Route
              path="/vendors/:id/underwriting/coverage"
              element={<VendorUnderwritingCoveragePage />}
            />

            <Route
              path="/vendors/:id/underwriting/matching"
              element={<VendorTransactionMatchingPage />}
            />

            <Route
              path="/vendors/:id/underwriting/financials"
              element={<VendorDetailedFinancialsPage />}
            />
          </Route>

          <Route
            path="/vendors/:id/notes"
            element={<VendorNotesPage />}
          />

          <Route
            path="/vendors/:id/uccs"
            element={<VendorUccsPage />}
          />

          <Route
            path="/vendors/:id/invoices"
            element={<VendorInvoicesPage />}
          />

          <Route
            path="/vendors/:id/in-flights"
            element={<VendorInFlightsPage />}
          />

          <Route
            path="/vendors/:id/scheduled-collections"
            element={<VendorScheduledVendorCollectionsPage />}
          />

          <Route
            path="/vendors/:id/restrictions"
            element={<VendorRestrictionsPage />}
          />

          <Route
            path="/vendors/:id/client-mapping-overrides"
            element={<VendorClientMappingOverridesPage />}
          />

          <Route
            path="/vendors/:id/audit"
            element={<VendorAuditPage />}
          />

          <Route
            path="/vendors/:id/verification-documents"
            element={<VendorVerificationDocumentsPage />}
          />

          <Route
            path="/vendors/:id/adjustment-groups"
            element={<VendorAdjustmentGroupsPage />}
          />

          <Route
            path="/vendors/:id/service-agreements"
            element={<VendorServiceAgreementsPage />}
          />

          <Route
            path="/vendors/:id/user-accounts"
            element={<VendorUserAccountsPage />}
          />

          <Route
            path="/vendors/:id/activity"
            element={<VendorActivityPage />}
          />

          <Route
            path="/vendors/:id/events"
            element={<VendorEventsPage />}
          />

          <Route
            path="/vendors/:id/vendor-email-sequences"
            element={<VendorEmailSequencesPage />}
          />

          <Route
            path="/vendors/:id/vendor-email-sequences/:sequenceId"
            element={<VendorEmailSequenceDetailPage />}
          />

          <Route
            path="/vendors/:id/features"
            element={<VendorFeaturesPage />}
          />
        </Route>

        <Route
          path="/clients"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ClientsPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="/clients/clients" />}
          />

          <Route
            path="/clients/clients"
            element={<ClientsClientsPage />}
          />

          <Route
            path="/clients/clients/:id"
            element={<ClientsClientPage />}
          />

          <Route
            path="/clients/client-mappings"
            element={<ClientsClientMappingsPage />}
          />

          <Route
            path="/clients/client-mappings/partners/:partnerId/external-clients/:externalClientId"
            element={<ClientsClientMappingPage />}
          />

          <Route
            path="/clients/client-exclusions"
            element={<ClientsExclusionsPage />}
          />

          <Route
            path="/clients/client-exclusions/partners/:partnerId/external-clients/:externalClientId"
            element={<ClientsClientExclusionPage />}
          />

          <Route
            path="/clients/vendor-restrictions"
            element={<ClientsVendorClientRestrictionsPage />}
          />

          <Route
            path="/clients/consolidated-clients"
            element={<ClientsConsolidatedClientsPage />}
          />
        </Route>

        <Route
          path="/programs"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ProgramsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/programs/:code/partners/:partnerId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ProgramPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/enablement-v2"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <EnablementV2Page />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoices"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoicesPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoice-approvals"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceApprovalsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoice-quarantines"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <InvoiceQuarantinesPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoices/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoicePage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoices/:id/documents/:documentId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceDocumentPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendors/:vendorId/serviceAgreements/:serviceAgreementId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorServiceAgreementPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partners/:partnerId/vendors/:vendorId/serviceAgreements/:serviceAgreementId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorServiceAgreementPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendors/:id/documents/:documentId"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorDocumentPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoice-line-items"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceLineItemsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/customer-batch-transfers"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <TransfersPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/settings"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <SettingsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoice-line-items/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceLineItemPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/onboardings"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <OnboardingsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/onboardings/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <OnboardingPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/projections"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ProjectionsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/tasks"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <TasksPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/reports"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ReportsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/key-performance-indicators"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <KeyPerformanceIndicatorsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/accelerated-payout-statistics"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AcceleratedPayoutStatisticsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/partner-integrations"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <PartnerIntegrations />
            </CognitoRoute>
          )}
        />
        <Route
          path="/configuration-settings"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ConfigurationSettingsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/external-vendor-statistics"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ExternalVendorStatistics />
            </CognitoRoute>
          )}
        />
        <Route
          path="/external-invoices"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <ExternalInvoicesPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/data-reconciliation"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <DataReconciliationPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/adjustment-groups/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AdjustmentGroupPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/adjustments"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AdjustmentsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/invoice-adjustment-approvals"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceAdjustmentApprovals />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendor-documents"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorDocumentsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/global-search"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <GlobalSearchResultsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/revenue"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <RevenuePage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/action-items"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ActionItemsBoardPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/cohorts"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <CohortsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/svb-loan-tape"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <SvbLoanTapeReportPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/yield-monitoring"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <YieldMonitoringPlatformPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/ondemand-inflights"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <EligibleInvoicesPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/anomalies"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AnomaliesPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/anomalies/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AnomalyPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/adjustments/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AdjustmentPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/underwriting"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <UnderwritingPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="/underwriting/transactions" />}
          />

          <Route
            path="/underwriting/transactions"
            element={<TransactionLabelsPage />}
          />

          <Route
            path="/underwriting/transactions/:transactionId"
            element={<TransactionLabelDetailPage />}
          />

          <Route
            path="/underwriting/coverage"
            element={<TransactionLabelCoveragePage />}
          />
        </Route>

        <Route
          path="/collections"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <CollectionsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/collections/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <CollectionPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/factoring-collections"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <FactoringCollectionsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/reconciliation"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ReconciliationPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/batch-sweeps"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <BatchSweepsPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/batch-sweeps/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <BatchSweepPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/reconciliation/partners/:partnerId/payload/:payloadIdentifier"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ReconciliationPayloadPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/service-agreements"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ServiceAgreements />
            </CognitoRoute>
          )}
        />

        <Route
          path="/service-agreements/:type/create"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <CreateServiceAgreementPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/service-agreements/:type/:version/update"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <UpdateServiceAgreementPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partner-service-agreements"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <PartnerServiceAgreements />
            </CognitoRoute>
          )}
        />

        <Route
          path={`/partner-service-agreements/partners/:partnerId/create`}
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <CreatePartnerServiceAgreementPage />
            </CognitoRoute>
          )}
        />

        <Route
          path={`/partner-service-agreements/partners/:partnerId/versions/:version/update`}
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <UpdatePartnerServiceAgreementPage partnerId={api.Partners.JLL} />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partner-rev-shares"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <PartnerRevSharesPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/partner-rev-shares/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <PartnerRevSharePage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/facilitators"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <FacilitatorsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/facilitators/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <FacilitatorsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/program-promotions"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ProgramPromotionsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/program-promotions/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ProgramPromotionDetailPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/email-sequences"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <EmailSequencesLayout />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<EmailSequencesPage />}
          />

          <Route
            path="/email-sequences/vendor-sequences"
            element={<VendorEmailSequencesOverviewPage />}
          />

          <Route
            path="/email-sequences/audit-log"
            element={<EmailSequenceAuditLogPage />}
          />

          <Route
            path="/email-sequences/audit-log/:id"
            element={<EmailSequenceAuditLogEntryPage />}
          />
        </Route>

        <Route
          path="/email-sequences/:id"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <EmailSequenceDetailPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/email-templates"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <EmailTemplatesLayout />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="/email-templates/preview" replace />}
          />

          <Route
            path="/email-templates/preview"
            element={<EmailTemplatesPage />}
          />

          <Route
            path="/email-templates/conditions"
            element={<EmailTemplateConditionsPage />}
          />
        </Route>

        <Route
          path="/faq-configuration"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <FaqsLayout />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="/faq-configuration/configuration" replace />}
          />

          <Route
            path="/faq-configuration/configuration"
            element={<FaqConfigurationPage />}
          />

          <Route
            path="/faq-configuration/defaults"
            element={<FaqDefaultsPage />}
          />

          <Route
            path="/faq-configuration/audit"
            element={<FaqAuditPage />}
          />
        </Route>

        <Route
          path="/factoring-access"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <FactoringAccessPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendor-surveys"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorSurveysPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/uccs"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <UccsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/vendor-sub-accounts"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <VendorSubAccountsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/abandoned-users"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AbandonedUsersPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/ai-assistant"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <AiAssistantPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/generate-msa-preview"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <GenerateMsaPreviewPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/vendor-csv-import"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <VendorCsvImportPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/scheduled-vendor-collections"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ScheduledVendorCollections />
            </CognitoRoute>
          )}
        />
        <Route
          path="/scheduled-vendor-collections/:identifier"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <ScheduledVendorCollectionPage />
            </CognitoRoute>
          )}
        />
        <Route
          path="/invoices/:invoiceId/scheduled-invoice-collections"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <InvoiceScheduledInvoiceCollectionsPage />
            </CognitoRoute>
          )}
        />

        <Route
          path="/transaction-matching"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <TransactionMatchExplorerPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="dashboard" replace />}
          />
          <Route
            path="dashboard"
            element={<TransactionMatchingDashboardPage />}
          />
          <Route
            index
            path="matches"
            element={<TransactionMatchExplorerMatchesPage />}
          />
          <Route
            path="matches/:matchId"
            element={<TransactionMatchDetailPage />}
          />
          <Route
            path="runs"
            element={<TransactionMatchExplorerRunsPage />}
          />
          <Route
            path="parameters"
            element={<TransactionMatchExplorerParametersPage />}
          />
          <Route
            path="parameters/:id"
            element={<TransactionMatchExplorerParameterDetailPage />}
          />
        </Route>

        <Route
          path="/portfolio-monitoring"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS, AuthenticationGroup.FINANCIERS]}
            >
              <PortfolioMonitoringPage />
            </CognitoRoute>
          )}
        >
          <Route
            index
            element={<Navigate to="overview" replace />}
          />
          <Route
            path="overview"
            element={<PortfolioMonitoringOverviewPage />}
          />
          <Route
            path="vendors"
            element={<PortfolioMonitoringVendorsPage />}
          />
          <Route
            path="buyers"
            element={<PortfolioMonitoringBuyersPage />}
          />
          <Route
            path="trading-partners"
            element={<PortfolioMonitoringTradingPartnersPage />}
          />
          <Route
            path="limits"
            element={<PortfolioMonitoringLimitsPage />}
          />
          <Route
            path="controls"
            element={<PortfolioMonitoringControlsPage />}
          />
        </Route>

        <Route
          path="/permissions"
          element={(
            <CognitoRoute
              authorizedRoles={[AuthenticationGroup.OPERATORS]}
            >
              <PermissionsPage />
            </CognitoRoute>
          )}
        />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
